import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, BehaviorSubject, tap, map } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { ErrorHandlingService } from '../../../../../core/services/error-handling.service';
import {
  ApiResponsePaginated,
  BehaviorStrategyItem,
  BehaviorStrategyQueryParams,
  BehaviorStrategyResponse,
  CreateModificationStrategyPayload,
  CreateOwnStrategyPayload,
  CreateStrategyPayload,
  PublishStrategyQueryParams,
  StrategyItem,
  StrategyQueryParams,
  UpdateStrategyStatusPayload,
} from './strategy.interface';
import { ApiResponse } from '../../../../../core/models/api-response.models';

@Injectable({
  providedIn: 'root',
})
export class ManageBehaviorStrategiesService {
  private readonly API_URL = `${environment.apiUrl}/Strategy`;
  strategiesSubject =
    new BehaviorSubject<ApiResponsePaginated<StrategyItem> | null>(null);
  strategies$ = this.strategiesSubject.asObservable();

  readonly publishStrategiesSubject =
    new BehaviorSubject<ApiResponsePaginated<StrategyItem> | null>(null);
  publishStrategies$ = this.publishStrategiesSubject.asObservable();

  private readonly behaviorStrategiesSubject = new BehaviorSubject<
    BehaviorStrategyItem[]
  >([]);
  behaviorStrategies$ = this.behaviorStrategiesSubject.asObservable();

  private readonly totalRowsSubject = new BehaviorSubject<number>(0);
  totalRows$ = this.totalRowsSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandler: ErrorHandlingService
  ) {}

  getStrategyList(
    queryParams: StrategyQueryParams
  ): Observable<ApiResponsePaginated<StrategyItem>> {
    const params = new HttpParams()
      .set('PageSize', queryParams.pageSize.toString())
      .set('Page', queryParams.pageIndex.toString())
      .set('Sort', queryParams.sortField || '')
      .set('SortOrder', queryParams.sortOrder?.toString() || '0')
      .set('strategyType', queryParams.strategyType?.toString() || '') // Fixed typo
      .set('caseBehaviorId', queryParams.caseBehaviorId?.toString() || '');

    return this.http
      .get<
        ApiResponsePaginated<StrategyItem>
      >(`${this.API_URL}/list`, { params })
      .pipe(
        catchError(error => this.errorHandler.handleError(error)),
        tap(response => {
          this.strategiesSubject.next(response);
        })
      );
  }

  getBehaviorStrategyList(
    queryParams: BehaviorStrategyQueryParams
  ): Observable<BehaviorStrategyResponse> {
    const params = new HttpParams()
      .set('PageSize', queryParams.pageSize.toString())
      .set('Page', queryParams.pageIndex.toString())
      .set('Sort', queryParams.sortField || '')
      .set('SortOrder', queryParams.sortOrder?.toString() || '0')
      .set('CaseBehaviorId', queryParams.caseBehaviorId?.toString() || '');

    return this.http
      .get<BehaviorStrategyResponse>(
        `${this.API_URL}/get-behaviorstrategy-list`,
        {
          params,
        }
      )
      .pipe(
        catchError(error => this.errorHandler.handleError(error)),
        tap(response => {
          if (response?.result) {
            if (response.result.items) {
              this.behaviorStrategiesSubject.next(response.result.items);
            }
            if (response.result.totalRows !== undefined) {
              this.totalRowsSubject.next(response.result.totalRows);
            }
          }
        })
      );
  }

  getPublishStrategyList(
    queryParams: PublishStrategyQueryParams
  ): Observable<ApiResponsePaginated<StrategyItem>> {
    const params = new HttpParams()
      .set('PageSize', queryParams.pageSize.toString())
      .set('Page', queryParams.pageIndex.toString())
      .set('SortOrder', queryParams.sortOrder?.toString() || '0')
      .set('organizationId', queryParams.organizationId.toString())
      .set('caseBehaviorId', queryParams.caseBehaviorId.toString())
      .set('Sort', queryParams.sortField || '');

    return this.http
      .get<
        ApiResponsePaginated<StrategyItem>
      >(`${environment.apiUrl}/BehaviorStrategyApproval/publish-strategy-list`, { params })
      .pipe(
        tap(response => {
          if (response) {
            this.publishStrategiesSubject.next(response);
          }
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  createStrategy(payload: CreateStrategyPayload): Observable<void> {
    return this.http
      .post<void>(`${this.API_URL}/create`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  createOwnStrategy(payload: CreateOwnStrategyPayload): Observable<void> {
    return this.http
      .post<void>(`${this.API_URL}/create-own-strategy`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteStrategy(strategyId: number): Observable<void> {
    return this.http
      .delete<void>(`${this.API_URL}/${strategyId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  createModificationStrategy(
    payload: CreateModificationStrategyPayload
  ): Observable<void> {
    return this.http
      .post<void>(`${this.API_URL}/create-modification-strategy`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateStrategyStatus(payload: UpdateStrategyStatusPayload): Observable<void> {
    return this.http
      .put<void>(`${this.API_URL}/update-status`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getBehaviorStrategyById(strategyId: string): Observable<StrategyItem | null> {
    const params = new HttpParams().set('strategyId', strategyId.toString());

    return this.http
      .get<
        ApiResponse<StrategyItem | null>
      >(`${this.API_URL}/get-behaviour-strategy-by-id`, { params })
      .pipe(
        map(response => response.result),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getBehaviorStrategyDetailsById(
    strategyId: number,
    behaviorId?: number
  ): Observable<StrategyItem | null> {
    let params = new HttpParams().set('strategyId', strategyId.toString());

    if (behaviorId) {
      params = params.set('caseBehaviorId', behaviorId.toString());
    }

    return this.http
      .get<
        ApiResponse<StrategyItem | null>
      >(`${this.API_URL}/get-behaviour-strategy-details-by-id`, { params })
      .pipe(
        map(response => response.result),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  approveStrategy(
    behaviorStrategyId: string,
    payload: { isApproved: boolean; comments: string }
  ): Observable<void> {
    return this.http
      .post<void>(
        `${environment.apiUrl}/BehaviorStrategyApproval/approval-strategy?BehaviorStrategyId=${behaviorStrategyId}`,
        payload
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
